// ═══════════════════════════════════════════════════════════════════════════
// ADMIN: GENERIC ROLES
// Migration S4.2 du plan TS+Vite (2026-05-10) : .js → .ts strict.
// ═══════════════════════════════════════════════════════════════════════════
//
// Migration i18n (mai 2026, suite des témoins stages + origins).

interface GenericRole {
  id: number;
  code: string;
  name: string;
  is_team: boolean;
}

declare global {
  interface Window {
    renderGenericRolesPage: typeof renderGenericRolesPage;
    showRoleModal: typeof showRoleModal;
  }
}

// La route est gardée par `generic_roles.read`, une clé de menu qui n'autorise
// pas l'écriture : sans ce test, les CTA étaient rendus à tout le monde et le
// serveur répondait 403 au clic (revue front §I3).
const canWriteGenericRoles = (): boolean =>
  !!(window.hasPermission && window.hasPermission('generic_roles.write'));

pages.adminGenericRoles = async function () {
  const canWrite = canWriteGenericRoles();
  setHeader(
    t('generic_roles.page_title'),
    canWrite
      ? `<button class="btn btn-secondary ml-auto" id="btn-ei-generic-roles" style="margin-right:6px">${esc(t('common.export_import'))}</button>` +
          `<button class="btn btn-primary" id="btn-new-role">${esc(t('generic_roles.btn.add'))}</button>`
      : '',
  );
  setContent(`<div class="loading">${esc(t('common.loading'))}</div>`);
  try {
    const roles = (await api.getGenericRoles()) as GenericRole[];
    renderGenericRolesPage(roles);
    document.getElementById('btn-ei-generic-roles')?.addEventListener('click', () => {
      const examples = window.EXPORT_EXAMPLES['generic-roles'];
      if (!examples) return;
      window.showExportImportModal(
        'generic-roles',
        t('generic_roles.page_title'),
        examples.json,
        examples.csv,
      );
    });
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : t('common.error');
    setContent(`<div class="error">${esc(msg)}</div>`);
  }
};

function renderGenericRolesPage(roles: GenericRole[]): void {
  let sortKey: 'code' | 'name' | 'is_team' = 'code';
  let sortDir: 'asc' | 'desc' = 'asc';

  const sort = (arr: ReadonlyArray<GenericRole>): GenericRole[] =>
    [...arr].sort((a, b) => {
      const va: string | number =
        sortKey === 'name'
          ? a.name.toLowerCase()
          : sortKey === 'is_team'
            ? a.is_team
              ? 1
              : 0
            : a.code;
      const vb: string | number =
        sortKey === 'name'
          ? b.name.toLowerCase()
          : sortKey === 'is_team'
            ? b.is_team
              ? 1
              : 0
            : b.code;
      return (va < vb ? -1 : va > vb ? 1 : 0) * (sortDir === 'asc' ? 1 : -1);
    });

  const th = (key: string, lbl: string): string => {
    const active = sortKey === key;
    return `<th data-sort-gr="${key}" style="cursor:pointer;user-select:none">${esc(lbl)} <span style="font-size:10px">${active ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}</span></th>`;
  };

  const renderTable = (): string => {
    const rows = sort(roles)
      .map(
        (r) => `<tr>
      <td style="cursor:${canWriteGenericRoles() ? 'pointer' : 'default'};font-family:var(--font-mono);font-size:12px;font-weight:600;${canWriteGenericRoles() ? 'text-decoration:underline' : ''}" ${canWriteGenericRoles() ? `data-click-role="${r.id}"` : ''}>${esc(r.code)}</td>
      <td style="cursor:${canWriteGenericRoles() ? 'pointer' : 'default'}" ${canWriteGenericRoles() ? `data-click-role="${r.id}"` : ''}>${esc(r.name)}</td>
      <td style="font-size:12px">${
        r.is_team
          ? `<span style="background:var(--accent-bg);color:var(--accent);font-size:11px;padding:1px 7px;border-radius:8px">${esc(t('generic_roles.type.team'))}</span>`
          : `<span style="color:var(--text3);font-size:12px">${esc(t('generic_roles.type.individual'))}</span>`
      }</td>
      <td style="white-space:nowrap">${
        canWriteGenericRoles()
          ? `<button class="btn btn-secondary btn-icon" data-edit-role="${r.id}" title="${esc(t('common.edit'))}">✎</button>
        <button class="btn btn-danger btn-icon" data-del-role="${r.id}" data-role-name="${esc(r.name)}" style="margin-left:4px" title="${esc(t('common.delete'))}">✕</button>`
          : ''
      }</td>
    </tr>`,
      )
      .join('');
    return `<div class="card" id="gr-card"><div class="table-wrap"><table>
      <thead><tr>${th('code', t('generic_roles.col.code'))}${th('name', t('generic_roles.col.name'))}${th('is_team', t('generic_roles.col.type'))}<th></th></tr></thead>
      <tbody>${rows || `<tr><td colspan="4" class="empty">${esc(t('generic_roles.empty'))}</td></tr>`}</tbody>
    </table></div></div>`;
  };

  const reRender = (): void => {
    const card = document.getElementById('gr-card');
    if (card) card.outerHTML = renderTable();
    wireGR();
  };
  setContent(renderTable());
  wireGR();

  function wireGR(): void {
    const card = document.getElementById('gr-card');
    if (!card) return;
    card.addEventListener('click', (e: Event) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;

      const sortTh = target.closest<HTMLElement>('[data-sort-gr]');
      if (sortTh) {
        const k = sortTh.dataset['sortGr'] as 'code' | 'name' | 'is_team';
        if (sortKey === k) sortDir = sortDir === 'asc' ? 'desc' : 'asc';
        else {
          sortKey = k;
          sortDir = 'asc';
        }
        reRender();
        return;
      }
      const clickCell = target.closest<HTMLElement>('[data-click-role]');
      if (clickCell && !target.closest('button')) {
        const id = parseInt(clickCell.dataset['clickRole'] || '0');
        const r = roles.find((x) => x.id === id);
        if (r) showRoleModal(r, roles, reRender);
        return;
      }
      const editBtn = target.closest<HTMLElement>('[data-edit-role]');
      if (editBtn) {
        const id = parseInt(editBtn.dataset['editRole'] || '0');
        const r = roles.find((x) => x.id === id);
        if (r) showRoleModal(r, roles, reRender);
        return;
      }
      const delBtn = target.closest<HTMLElement>('[data-del-role]');
      if (delBtn) {
        const id = parseInt(delBtn.dataset['delRole'] || '0');
        const name = delBtn.dataset['roleName'] || '';
        openModal(
          t('generic_roles.modal.delete.title'),
          `<p>${t('generic_roles.modal.delete.message', { name })}</p>`,
          async () => {
            try {
              await api.deleteGenericRole(id);
            } catch (e) {
              // 409 si le rôle est encore référencé (pas de delete cascade).
              toast(e instanceof Error ? e.message : t('common.error'), 'error');
              return;
            }
            closeModal();
            toast(t('generic_roles.toast.deleted'));
            roles.splice(
              roles.findIndex((x) => x.id === id),
              1,
            );
            reRender();
          },
          t('common.delete'),
        );
      }
    });
    document.getElementById('btn-new-role')?.addEventListener('click', () =>
      showRoleModal(null, roles, (created) => {
        roles.push(created);
        reRender();
      }),
    );
  }
}

function showRoleModal(
  role: GenericRole | null,
  roles: GenericRole[],
  onSaved: (saved: GenericRole) => void,
): void {
  const isNew = !role;
  const body = `
    <div class="form-row">
      <div class="form-group" style="flex:0 0 80px"><label>${esc(t('generic_roles.field.code.label'))}</label>
        <input type="text" id="rm-code" maxlength="5" value="${esc(role?.code || '')}"
          style="text-transform:uppercase;font-family:var(--font-mono);width:100%"></div>
      <div class="form-group" style="flex:1"><label>${esc(t('common.field.name'))}</label>
        <input type="text" id="rm-name" value="${esc(role?.name || '')}" placeholder="${esc(t('generic_roles.field.name.placeholder'))}" style="width:100%"></div>
    </div>
    <div class="form-group" style="margin-top:8px">
      <label style="display:flex;align-items:center;gap:6px;cursor:pointer">
        <input type="checkbox" id="rm-team" ${role?.is_team ? 'checked' : ''}>
        ${esc(t('generic_roles.field.is_team.label'))}
      </label>
    </div>`;
  openModal(
    isNew ? t('generic_roles.modal.create.title') : t('generic_roles.modal.update.title'),
    body,
    async () => {
      const codeEl = document.getElementById('rm-code') as HTMLInputElement | null;
      const nameEl = document.getElementById('rm-name') as HTMLInputElement | null;
      const teamEl = document.getElementById('rm-team') as HTMLInputElement | null;
      const code = (codeEl?.value || '').trim().toUpperCase();
      const name = (nameEl?.value || '').trim();
      if (!code || !name) {
        toast(t('generic_roles.error.code_name_required'), 'error');
        return;
      }
      const is_team = teamEl?.checked ?? false;
      try {
        const result = (
          isNew
            ? await api.createGenericRole({ code, name, is_team })
            : await api.updateGenericRole((role as GenericRole).id, { code, name, is_team })
        ) as GenericRole;
        closeModal();
        toast(isNew ? t('generic_roles.toast.created') : t('generic_roles.toast.updated'));
        if (isNew) onSaved(result);
        else {
          const idx = roles.findIndex((x) => x.id === (role as GenericRole).id);
          if (idx !== -1) roles[idx] = result;
          onSaved(result);
        }
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : t('common.error');
        toast(msg, 'error');
      }
    },
    isNew ? t('common.add_short') : t('common.save'),
  );
  setTimeout(() => {
    document.getElementById('rm-code')?.addEventListener('input', (e: Event) => {
      const target = e.target as HTMLInputElement | null;
      if (target) target.value = target.value.toUpperCase();
    });
  }, 0);
}

// ── ESM globals shim (lot B.8 + S4.2) ──
window.renderGenericRolesPage = renderGenericRolesPage;
window.showRoleModal = showRoleModal;

export {};
